// The picture side of the video upscaler: a few WebGL2 passes that enlarge one frame.
//   1. noise cleanup (optional): a small edge-preserving blur at the video's own size, so that grain and compression blocks are not enlarged;
//   2. Lanczos-3 enlargement, in two passes (across, then down): much sharper than the browser's own smooth stretch;
//   3. contrast-adaptive sharpening, then a limit that stops the bright and dark halos ("ringing") that sharp enlargement can leave around edges.
// It works on the graphics card, so it is fast enough to follow a playing video.

const VERT = `#version 300 es
in vec2 p;
out vec2 uv;
void main() { uv = p * 0.5 + 0.5; gl_Position = vec4(p, 0.0, 1.0); }`;

const DENOISE = `#version 300 es
precision highp float;
uniform sampler2D tex; uniform vec2 px; uniform float s;
in vec2 uv; out vec4 o;
void main() {
  vec3 c = texture(tex, uv).rgb;
  float inv = 1.0 / (2.0 * s * s + 1e-6);
  vec3 acc = c; float ws = 1.0;
  for (int j = -2; j <= 2; j++) for (int i = -2; i <= 2; i++) {
    if (i == 0 && j == 0) continue;
    vec3 n = texture(tex, uv + vec2(float(i), float(j)) * px).rgb;
    vec3 d = n - c;
    float w = exp(-float(i * i + j * j) * 0.2) * exp(-dot(d, d) * inv);
    acc += n * w; ws += w;
  }
  o = vec4(acc / ws, 1.0);
}`;

// one axis of the Lanczos enlargement; AXIS is 0 (across) or 1 (down)
const lanczos = (axis) => `#version 300 es
precision highp float;
uniform sampler2D tex; uniform vec2 size;
in vec2 uv; out vec4 o;
float lz(float x) {
  x = abs(x);
  if (x < 1e-4) return 1.0;
  if (x >= 3.0) return 0.0;
  float t = 3.14159265 * x;
  return 3.0 * sin(t) * sin(t / 3.0) / (t * t);
}
void main() {
  float n = size[${axis}];
  float at = uv[${axis}] * n - 0.5;
  float f = floor(at);
  vec3 acc = vec3(0.0); float ws = 0.0;
  for (int i = -2; i <= 3; i++) {
    float t = f + float(i);
    float w = lz(at - t);
    float c = (clamp(t, 0.0, n - 1.0) + 0.5) / n;
    vec2 q = ${axis === 0 ? "vec2(c, uv.y)" : "vec2(uv.x, c)"};
    acc += texture(tex, q).rgb * w; ws += w;
  }
  o = vec4(acc / ws, 1.0);
}`;

const FINAL = `#version 300 es
precision highp float;
uniform sampler2D up; uniform sampler2D src;
uniform vec2 upPx; uniform vec2 srcSize; uniform float sharp; uniform float reach;
in vec2 uv; out vec4 o;
void main() {
  vec3 c = texture(up, uv).rgb;
  // contrast adaptive sharpening: the flatter the surroundings, the more an edge is sharpened (so noise and already crisp edges are left alone)
  vec2 r = upPx * reach;
  vec3 a = texture(up, uv + vec2(0.0, -r.y)).rgb;
  vec3 b = texture(up, uv + vec2(-r.x, 0.0)).rgb;
  vec3 d = texture(up, uv + vec2(r.x, 0.0)).rgb;
  vec3 e = texture(up, uv + vec2(0.0, r.y)).rgb;
  vec3 mn = min(min(min(a, b), min(d, e)), c);
  vec3 mx = max(max(max(a, b), max(d, e)), c);
  vec3 amp = sqrt(clamp(min(mn, 1.0 - mx) / (mx + 1e-4), 0.0, 1.0));
  vec3 w = amp * (-1.0 / mix(10.0, 4.0, sharp));
  vec3 res = ((a + b + d + e) * w + c) / (1.0 + 4.0 * w);
  // no halos: the result may not go far beyond the brightest and darkest of the video's own pixels around this point
  vec2 base = floor(uv * srcSize);
  vec3 lo = vec3(1.0); vec3 hi = vec3(0.0);
  for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) {
    vec3 n = texture(src, (clamp(base + vec2(float(i), float(j)), vec2(0.0), srcSize - 1.0) + 0.5) / srcSize).rgb;
    lo = min(lo, n); hi = max(hi, n);
  }
  vec3 room = (hi - lo) * (0.12 + 0.4 * sharp) + 0.004;
  o = vec4(clamp(res, lo - room, hi + room), 1.0);
}`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(`Shader error: ${gl.getShaderInfoLog(shader)}`);
  return shader;
}

export function program(gl, fragment) {
  const prog = gl.createProgram();
  gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
  gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, fragment));
  gl.bindAttribLocation(prog, 0, "p");
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(`Shader link error: ${gl.getProgramInfoLog(prog)}`);
  const uniforms = {};
  const count = gl.getProgramParameter(prog, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i += 1) { const name = gl.getActiveUniform(prog, i).name; uniforms[name] = gl.getUniformLocation(prog, name); }
  return { prog, uniforms };
}

export function supported() {
  try {
    return !!document.createElement("canvas").getContext("webgl2");
  } catch {
    return false;
  }
}

export class Upscaler {
  constructor(canvas) {
    this.canvas = canvas;
    const gl = canvas.getContext("webgl2", { antialias: false, alpha: false, preserveDrawingBuffer: true, powerPreference: "high-performance" });
    if (!gl) throw new Error("This browser cannot use the graphics card for video (WebGL 2 is off).");
    this.gl = gl;
    this.maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    this.denoise = program(gl, DENOISE);
    this.across = program(gl, lanczos(0));
    this.down = program(gl, lanczos(1));
    this.final = program(gl, FINAL);
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    this.vao = vao;
    this.buffer = buffer;
    this.targets = {};
    this.srcTexture = this.texture("nearest");
    this.size = [0, 0];
    this.srcSize = [0, 0];
  }

  texture(filter) {
    const { gl } = this;
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    const f = filter === "linear" ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  // a texture that can be drawn into, of a size: { tex, fbo, w, h }, made again only when the size changes
  target(name, w, h, filter = "nearest") {
    const { gl } = this;
    const old = this.targets[name];
    if (old && old.w === w && old.h === h) return old;
    if (old) { gl.deleteTexture(old.tex); gl.deleteFramebuffer(old.fbo); }
    const tex = this.texture(filter);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    this.targets[name] = { tex, fbo, w, h };
    return this.targets[name];
  }

  setOutput(w, h) {
    if (Math.max(w, h) > this.maxSize) throw new Error(`This graphics card cannot make pictures of ${w}x${h} (its limit is ${this.maxSize}). Choose a smaller size.`);
    this.size = [w, h];
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
  }

  pass(prog, target, inputs, uniforms, w, h, before = null) {
    const { gl } = this;
    gl.useProgram(prog.prog);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fbo : null);
    gl.viewport(0, 0, w, h);
    inputs.forEach(([name, tex], unit) => {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(prog.uniforms[name], unit);
    });
    Object.entries(uniforms).forEach(([name, value]) => {
      const at = prog.uniforms[name];
      if (at === undefined || at === null) return;
      if (Array.isArray(value)) {
        if (value.length === 2) gl.uniform2f(at, value[0], value[1]);
        else if (value.length === 3) gl.uniform3f(at, value[0], value[1], value[2]);
        else gl.uniform4f(at, value[0], value[1], value[2], value[3]);
      } else gl.uniform1f(at, value);
    });
    if (before) before(gl, prog);                                          // for what the helper cannot bind itself (a 3D texture)
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  // Draws `source` (a video, an image or a canvas) enlarged into the canvas. `sharp` and `denoise` are 0..1.
  render(source, { sharp = 0.4, denoise = 0 } = {}) {
    const { gl } = this;
    const sw = source.videoWidth || source.naturalWidth || source.width;
    const sh = source.videoHeight || source.naturalHeight || source.height;
    const [ow, oh] = this.size;
    if (!sw || !sh || !ow || !oh) return;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.srcTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    this.srcSize = [sw, sh];
    let input = this.srcTexture;
    if (denoise > 0.01) {
      const clean = this.target("clean", sw, sh);
      this.pass(this.denoise, clean, [["tex", this.srcTexture]], { px: [1 / sw, 1 / sh], s: 0.03 + 0.22 * denoise }, sw, sh);
      input = clean.tex;
    }
    const wide = this.target("across", ow, sh);
    this.pass(this.across, wide, [["tex", input]], { size: [sw, sh] }, ow, sh);
    const big = this.target("down", ow, oh, "linear");
    this.pass(this.down, big, [["tex", wide.tex]], { size: [ow, sh] }, ow, oh);
    const factor = Math.max(ow / sw, oh / sh);
    this.pass(this.final, null, [["up", big.tex], ["src", input]], { upPx: [1 / ow, 1 / oh], srcSize: [sw, sh], sharp, reach: Math.min(2.4, Math.max(1, factor * 0.55)) }, ow, oh);
  }

  dispose() {
    const { gl } = this;
    Object.values(this.targets).forEach((t) => { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fbo); });
    gl.deleteTexture(this.srcTexture);
    gl.deleteBuffer(this.buffer);
    const lose = gl.getExtension("WEBGL_lose_context");
    if (lose) lose.loseContext();
  }
}
